import { useApi } from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";
import { computed, type MaybeRef, unref } from "vue";

/** Status filter values for client leads API */
export type ClientLeadStatusFilter = 'awaiting_match' | 'matched' | 'all';

/**
 * Composable for fetching leads submitted by the authenticated client.
 * 
 * Uses the client leads API to retrieve a paginated list of the client's leads
 * with their matched experts.
 * 
 * @param statusFilter - Optional reactive filter for lead status
 *   - 'awaiting_match': Leads pending expert matching
 *   - 'matched': Leads that have been matched with experts
 *   - 'all': All leads (default)
 * 
 * @example
 * ```ts
 * const { leads, isLoading, refetch } = useClientLeads();
 * 
 * // With status filter
 * const statusRef = ref<ClientLeadStatusFilter>('awaiting_match');
 * const { leads } = useClientLeads(statusRef);
 * ```
 */
export function useClientLeads(statusFilter?: MaybeRef<ClientLeadStatusFilter | undefined>) {
  const api = useApi();

  const status = computed(() => unref(statusFilter));

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: computed(() => ['client', 'leads-v2', 'list', status.value]),
    queryFn: async () => {
      const query: { limit?: number; status?: ClientLeadStatusFilter } = { limit: 20 };
      if (status.value && status.value !== 'all') {
        query.status = status.value;
      }
      return api.client.leadsV2.listLeads({ query });
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    throwOnError: true,
  });

  const leads = computed(() => data.value?.data || []);

  return {
    leads,
    isLoading,
    error,
    refetch,
  };
}

